import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { store } from './store';
import { RootState } from './store';
import { restoreAuth } from './store/slices/authSlice';
import { useScrollToTop } from './hooks/useScrollToTop';
import ProtectedRoute from './components/auth/ProtectedRoute';

// ScrollToTop component to handle page navigation scrolling
const ScrollToTop: React.FC = () => {
  useScrollToTop();
  return null;
};

// Components
import LoginForm from './components/auth/LoginForm';
import UserLoginForm from './components/auth/UserLoginForm';
import DashboardLayout from './components/layout/DashboardLayout';
import DealerLayout from './components/layout/DealerLayout';
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetails from './pages/ProjectDetails';
import Profile from './pages/Profile';
import UserProfile from './pages/user/UserProfile';
import UserWallet from './pages/user/UserWallet';
import Marketplace from './pages/user/Marketplace';
import MarketplaceDetail from './pages/user/MarketplaceDetail';
import PublicSoldListings from './pages/PublicSoldListings';
import MarketplacePayment from './pages/user/MarketplacePayment';
import MarketplaceKYC from './pages/user/MarketplaceKYC';
import MarketplaceTransactions from './pages/user/MarketplaceTransactions';
import MarketplaceInquiries from './pages/user/MarketplaceInquiries';
import MembershipPlans from './pages/user/MembershipPlans';
import MembershipPurchase from './pages/user/MembershipPurchase';
import MembershipStatus from './pages/user/MembershipStatus';
import Apartments from './pages/user/Apartments';
import ApartmentDetail from './pages/user/ApartmentDetail';
import MyBookings from './pages/user/MyBookings';
import MyStayBookings from './pages/user/MyStayBookings';
import BrowseChildren from './pages/user/BrowseChildren';
import UserChildDetails from './pages/user/ChildDetails';
import MySponsoredChildren from './pages/user/MySponsoredChildren';
import UserSponsorshipUpdates from './pages/user/SponsorshipUpdates';
import DealerProfile from './pages/dealer/DealerProfile';
import DealerWallet from './pages/dealer/DealerWallet';
import DealerDashboard from './pages/dealer/DealerDashboard';
import DealerRegistration from './pages/dealer/DealerRegistration';
import DealerListings from './pages/dealer/DealerListings';
import DealerCreateListing from './pages/dealer/DealerCreateListing';
import DealerEditListing from './pages/dealer/DealerEditListing';
import DealerViewListing from './pages/dealer/DealerViewListing';
import DealerSoldListings from './pages/dealer/DealerSoldListings';
import SuperAdminProfile from './pages/admin/SuperAdminProfile';

// New Pages
import About from './pages/About';
import Services from './pages/Services';
import Blogs from './pages/Blogs';
import BlogDetails from './pages/BlogDetails';
import Testimonials from './pages/Testimonials';
import Contact from './pages/Contact';
import Solar from './pages/Solar';
import AppShowcase from './pages/AppShowcase';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Careers from './pages/Careers';
import JobDetails from './pages/JobDetails';
import ThankYou from './pages/ThankYou';

// Admin Pages
import AdminProjects from './pages/admin/AdminProjects';
import ViewProjectDetails from './pages/admin/ViewProjectDetails';
import AdminInvestments from './pages/admin/AdminInvestments';
import AdminUsers from './pages/admin/AdminUsers';
import AdminBankAccounts from './pages/admin/AdminBankAccounts';
import AdminWallet from './pages/admin/AdminWallet';
import AdminBlogs from './pages/admin/AdminBlogs';
import AddBlog from './pages/admin/AddBlog';
import EditBlog from './pages/admin/EditBlog';
import ViewBlog from './pages/admin/ViewBlog';
import AdminTestimonials from './pages/admin/AdminTestimonials';
import CreateTestimonial from './pages/admin/CreateTestimonial';
import EditTestimonial from './pages/admin/EditTestimonial';
import AdminPaymentMethods from './pages/admin/AdminPaymentMethods';
import AdminContactSubmissions from './pages/admin/AdminContactSubmissions';
import ContactSubmissionDetails from './pages/admin/ContactSubmissionDetails';
import AdminSolarLeads from './pages/admin/AdminSolarLeads';
import SolarLeadDetails from './pages/admin/SolarLeadDetails';
import CreateProject from './pages/admin/CreateProject';
import CTAManagement from './pages/admin/CTAManagement';
import CTAAdd from './pages/admin/CTAAdd';
import CTAEdit from './pages/admin/CTAEdit';
import Category from './pages/Category';

// Child Sponsorship Pages
import ChildSponsorship from './pages/admin/ChildSponsorship';
import CreateChild from './pages/admin/CreateChild';
import EditChild from './pages/admin/EditChild';
import ChildDetails from './pages/admin/ChildDetails';
import SponsorshipUpdates from './pages/admin/SponsorshipUpdates';
import CreateSponsorshipUpdate from './pages/admin/CreateSponsorshipUpdate';
import EditSponsorshipUpdate from './pages/admin/EditSponsorshipUpdate';
import MembershipPlanManagement from './pages/admin/MembershipPlanManagement';
import CreateMembershipPlan from './pages/admin/CreateMembershipPlan';
import EditMembershipPlan from './pages/admin/EditMembershipPlan';
import ViewMembershipPlan from './pages/admin/ViewMembershipPlan';
import MembershipMemberManagement from './pages/admin/MembershipMemberManagement';
import ViewMembership from './pages/admin/ViewMembership';
import EditMembership from './pages/admin/EditMembership';
import CreateMembership from './pages/admin/CreateMembership';
import ApartmentManagement from './pages/admin/ApartmentManagement';
import ViewApartment from './pages/admin/ViewApartment';
import EditApartment from './pages/admin/EditApartment';
import CreateApartment from './pages/admin/CreateApartment';
import BookingManagement from './pages/admin/BookingManagement';
import BookingDetails from './pages/admin/BookingDetails';

// Marketplace Pages
import MarketplaceListings from './pages/admin/MarketplaceListings';
import CreateMarketplaceListing from './pages/admin/CreateMarketplaceListing';
import ViewMarketplaceListing from './pages/admin/ViewMarketplaceListing';
import EditMarketplaceListing from './pages/admin/EditMarketplaceListing';
import AdminMarketplaceInquiries from './pages/admin/MarketplaceInquiries';
import MarketplaceInquiryDetails from './pages/admin/MarketplaceInquiryDetails';
import AdminMarketplaceTransactions from './pages/admin/MarketplaceTransactions';
import MarketplaceTransactionDetails from './pages/admin/MarketplaceTransactionDetails';
import MarketplaceSettings from './pages/admin/MarketplaceSettings';
import MarketplaceKYCRequests from './pages/admin/MarketplaceKYCRequests';
import MarketplaceKYCDetails from './pages/admin/MarketplaceKYCDetails';
import DealerManagement from './pages/admin/DealerManagement';
import DealerDetails from './pages/admin/DealerDetails';
import SoldListings from './pages/admin/SoldListings';
import PendingListings from './pages/admin/PendingListings';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminJobs from './pages/admin/AdminJobs';
import AdminJobForm from './pages/admin/AdminJobForm';
import AdminJobApplications from './pages/admin/AdminJobApplications';

// Styles
import 'react-toastify/dist/ReactToastify.css';
import { Toaster } from 'react-hot-toast';
import Users from './pages/Users';
import UserDetails from './pages/UserDetails';
import ProjectUpdate from './pages/admin/ProjectUpdate';
import Notification from './pages/Notification';
import TrophyTask from './pages/TrophyTask';
import Chatbot from './components/common/Chatbot';

// Nigerian Diaspora Pages
import NigerianDiasporaInvestors from './pages/NigerianDiasporaInvestors';
import UKNigerianDiaspora from './pages/UKNigerianDiaspora';
import USNigerianDiaspora from './pages/USNigerianDiaspora';
import EuropeNigerianDiaspora from './pages/EuropeNigerianDiaspora';
import AsiaNigerianDiaspora from './pages/AsiaNigerianDiaspora';

// Investment Pages
import Investment from './pages/Investment';
import RealEstateInvestmentNigeria from './pages/investment/RealEstateInvestmentNigeria';
import RealEstateInvestingFromAbroad from './pages/investment/RealEstateInvestingFromAbroad';
import CommercialPropertyInvestmentNigeria from './pages/investment/CommercialPropertyInvestmentNigeria';
import ResidentialPropertyInvestmentNigeria from './pages/investment/ResidentialPropertyInvestmentNigeria';
import FractionalPropertyInvestmentNigeria from './pages/investment/FractionalPropertyInvestmentNigeria';

// Conditional Marketplace Route Component
// Shows PublicSoldListings for unauthenticated users, Marketplace for authenticated users
const MarketplaceRoute: React.FC = () => {
  const { token, user } = useSelector((state: RootState) => state.auth);
  
  // If user is logged in, show Marketplace, otherwise show PublicSoldListings
  if (token && user) {
    return <Marketplace />;
  }
  
  return <PublicSoldListings />;
};

const AppRoutes: React.FC = () => {
  // Restore authentication state on app load
  React.useEffect(() => {
    store.dispatch(restoreAuth());
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Public Routes - Accessible to everyone */}
        <Route path="/" element={<HomePage />} />
        <Route path="/app" element={<AppShowcase />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/blog/:slug" element={<BlogDetails />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/:id" element={<JobDetails />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/solar" element={<Solar />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetails />} />
        <Route path="/trophy-tasks" element={<TrophyTask />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/user-login" element={<UserLoginForm />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/user/profile" element={<UserProfile />} />
        <Route path="/user/wallet" element={<UserWallet />} />
        <Route path="/marketplace" element={<MarketplaceRoute />} />
        <Route path="/marketplace/sold" element={<PublicSoldListings />} />
        <Route path="/marketplace/:id" element={<MarketplaceDetail />} />
        <Route path="/marketplace/:id/payment" element={<MarketplacePayment />} />
        <Route path="/marketplace/kyc" element={<MarketplaceKYC />} />
        <Route path="/marketplace/transactions" element={<MarketplaceTransactions />} />
        <Route path="/marketplace/inquiries" element={<MarketplaceInquiries />} />
        {/* Membership & Booking Routes */}
        <Route path="/membership/plans" element={<MembershipPlans />} />
        <Route path="/membership/purchase/:planId" element={<MembershipPurchase />} />
        <Route path="/membership/status" element={<MembershipStatus />} />
        <Route path="/apartments" element={<Apartments />} />
        <Route path="/apartments/my-bookings" element={<MyStayBookings />} />
        <Route path="/apartments/:id" element={<ApartmentDetail />} />
        <Route path="/bookings" element={<MyBookings />} />
        {/* Child Sponsorship Routes */}
        <Route path="/sponsor/children" element={<BrowseChildren />} />
        <Route path="/sponsor/child/:id" element={<UserChildDetails />} />
        <Route path="/sponsor/my-children" element={<MySponsoredChildren />} />
        <Route path="/sponsor/child/:id/updates" element={<UserSponsorshipUpdates />} />
        <Route path="/dealer/register" element={<DealerRegistration />} />
        
        {/* Dealer Routes - Wrapped in DealerLayout */}
        <Route path="/dealer" element={
          <ProtectedRoute requiredRole="DEALER">
            <DealerLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/dealer/dashboard" replace />} />
          <Route path="dashboard" element={<DealerDashboard />} />
          <Route path="profile" element={<DealerProfile />} />
          <Route path="wallet" element={<DealerWallet />} />
          <Route path="listings" element={<DealerListings />} />
          <Route path="listings/create" element={<DealerCreateListing />} />
          <Route path="listings/:listing_id" element={<DealerViewListing />} />
          <Route path="listings/:listing_id/edit" element={<DealerEditListing />} />
          <Route path="listings/sold" element={<DealerSoldListings />} />
        </Route>
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        
        {/* Nigerian Diaspora Routes */}
        <Route path="/nigerian-diaspora-investors" element={<NigerianDiasporaInvestors />} />
        <Route path="/nigerian-diaspora-investors/uk" element={<UKNigerianDiaspora />} />
        <Route path="/nigerian-diaspora-investors/us" element={<USNigerianDiaspora />} />
        <Route path="/nigerian-diaspora-investors/europe" element={<EuropeNigerianDiaspora />} />
        <Route path="/nigerian-diaspora-investors/asia" element={<AsiaNigerianDiaspora />} />

        {/* Investment Routes */}
        <Route path="/investment" element={<Investment />} />
        <Route path="/investment/real-estate-investment-nigeria" element={<RealEstateInvestmentNigeria />} />
        <Route path="/investment/real-estate-investing-from-abroad" element={<RealEstateInvestingFromAbroad />} />
        <Route path="/investment/commercial-property-investment-nigeria" element={<CommercialPropertyInvestmentNigeria />} />
        <Route path="/investment/residential-property-investment-nigeria" element={<ResidentialPropertyInvestmentNigeria />} />
        <Route path="/investment/fractional-property-investment-nigeria" element={<FractionalPropertyInvestmentNigeria />} />

          {/* Admin Routes - For both AU (Admin) and SU (Super Admin) users */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="create-project" element={<CreateProject />} />
          <Route path="projects" element={<AdminProjects />} />
          <Route path="projects/view" element={<ViewProjectDetails />} />
          <Route path="update-project" element={<ProjectUpdate />} />
          <Route path="investments" element={<AdminInvestments />} />
          <Route path="admin-users" element={<AdminUsers />} />
          <Route path="users" element={<Users />} />
          <Route path="user-details/:id" element={<UserDetails />} />
          <Route path="categories" element={<Category />} />
          <Route path="bank-accounts" element={<AdminBankAccounts />} />
          <Route path="wallet" element={<AdminWallet />} />
          <Route path="cta-management" element={<CTAManagement />} />
          <Route path="cta-management/add" element={<CTAAdd />} />
          <Route path="cta-management/edit/:id" element={<CTAEdit />} />
          <Route path="blogs" element={<AdminBlogs />} />
          <Route path="blogs/add" element={<AddBlog />} />
          <Route path="blogs/edit/:id" element={<EditBlog />} />
          <Route path="blogs/view/:id" element={<ViewBlog />} />
          
          <Route path="jobs" element={<AdminJobs />} />
          <Route path="jobs/add" element={<AdminJobForm />} />
          <Route path="jobs/edit/:id" element={<AdminJobForm />} />
          <Route path="jobs/applications" element={<AdminJobApplications />} />
          <Route path="jobs/applications/:jobId" element={<AdminJobApplications />} />

          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="testimonials/create" element={<CreateTestimonial />} />
          <Route path="testimonials/edit/:id" element={<EditTestimonial />} />
          <Route path="payment-methods" element={<AdminPaymentMethods />} />
          <Route path="profile" element={<Profile />} />
          <Route path="super-admin-profile" element={<SuperAdminProfile />} />
          <Route path="notifications" element={<Notification />} />
          <Route path="contact-submissions" element={<AdminContactSubmissions />} />
          <Route path="contact-submissions/:id" element={<ContactSubmissionDetails />} />
          
          {/* Solar Leads Management */}
          <Route path="solar-leads" element={<AdminSolarLeads />} />
          <Route path="solar-leads/:id" element={<SolarLeadDetails />} />
          
          {/* Child Sponsorship Routes */}
          <Route path="child-sponsorship" element={<ChildSponsorship />} />
          <Route path="child-sponsorship/create" element={<CreateChild />} />
          <Route path="child-sponsorship/edit/:id" element={<EditChild />} />
          <Route path="child-sponsorship/:id" element={<ChildDetails />} />
          <Route path="child-sponsorship/updates" element={<SponsorshipUpdates />} />
          <Route path="child-sponsorship/updates/create" element={<CreateSponsorshipUpdate />} />
          <Route path="child-sponsorship/updates/edit/:updateId" element={<EditSponsorshipUpdate />} />
          
          {/* Membership Management */}
          <Route path="membership-management" element={<MembershipPlanManagement />} />
          <Route path="membership-management/create" element={<CreateMembershipPlan />} />
          <Route path="membership-management/edit/:id" element={<EditMembershipPlan />} />
          <Route path="membership-management/view/:id" element={<ViewMembershipPlan />} />
          <Route path="memberships" element={<MembershipMemberManagement />} />
          <Route path="memberships/create" element={<CreateMembership />} />
          <Route path="memberships/view/:id" element={<ViewMembership />} />
          <Route path="memberships/edit/:id" element={<EditMembership />} />
          <Route path="apartments" element={<ApartmentManagement />} />
          <Route path="apartments/create" element={<CreateApartment />} />
          <Route path="apartments/view/:id" element={<ViewApartment />} />
          <Route path="apartments/edit/:id" element={<EditApartment />} />
          
          {/* Booking Management */}
          <Route path="bookings" element={<BookingManagement />} />
          <Route path="bookings/:id" element={<BookingDetails />} />
          
          {/* Marketplace Management */}
          <Route path="marketplace-listings" element={<MarketplaceListings />} />
          <Route path="marketplace-listings/create" element={<CreateMarketplaceListing />} />
          <Route path="marketplace-listings/view/:id" element={<ViewMarketplaceListing />} />
          <Route path="marketplace-listings/edit/:id" element={<EditMarketplaceListing />} />
          <Route path="marketplace-inquiries" element={<AdminMarketplaceInquiries />} />
          <Route path="marketplace-inquiries/view/:id" element={<MarketplaceInquiryDetails />} />
          <Route path="marketplace-transactions" element={<AdminMarketplaceTransactions />} />
          <Route path="marketplace-transactions/view/:id" element={<MarketplaceTransactionDetails />} />
          <Route path="marketplace-settings" element={<MarketplaceSettings />} />
          <Route path="marketplace-kyc-requests" element={<MarketplaceKYCRequests />} />
          <Route path="marketplace-kyc-requests/view/:id" element={<MarketplaceKYCDetails />} />
          
          {/* Dealer Management */}
          <Route path="dealer-management" element={<DealerManagement />} />
          <Route path="dealer-management/:id" element={<DealerDetails />} />
          
          {/* Sold Listings */}
          <Route path="sold-listings" element={<SoldListings />} />
          
          {/* Pending Listings */}
          <Route path="pending-listings" element={<PendingListings />} />

          {/* Reports & Analytics */}
          <Route path="reports" element={<AdminDashboard />} />
          
          {/* FAQ Management - Placeholder until dedicated component is created */}
          <Route path="faq" element={<Navigate to="/admin" replace />} />
        </Route>

        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1F2937',
            color: '#F9FAFB',
            border: '1px solid #374151',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#F9FAFB',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#F9FAFB',
            },
          },
        }}
      />

      {/* Global Chatbot - Visible on all pages except admin */}
      <Chatbot />
    </Router>
  );
};

import { IdleTimer } from './components/common/IdleTimer';

function App() {
  const [rehydrated, setRehydrated] = React.useState(false);

  React.useEffect(() => {
    store.dispatch(restoreAuth());
    setRehydrated(true);
  }, []);

  if (!rehydrated) {
    // Optionally show a loading spinner here    
    return null;
  }

  return (
    <>
      <div className="animated-app-bg"></div>
      <Provider store={store}>
        <div className="App ">
          <IdleTimer />
          <AppRoutes />
        </div>
      </Provider>
    </>
  );
}

export default App;